import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { ENABLE_JO_REGISTRY } from "../../utils/featureFlags";
import ChangePasswordModal from "../modals/ChangePasswordModal";
import { APP_VERSION } from "../../utils/version";
import { useState } from "react";
import NotificationBell from "./NotificationBell";

export default function MobileNavbar() {
  const { user, logout, isMobileRole, canAccessNIDashboard, canAccessNiDashboard, canAccessDashboard, canAccessManageAccounts, canViewRoles, canAccessXlsx, canAccessDatabaseBackups, canAccessJoRegistry, canViewLoginActivity, canAccessReports, canAccessMigration, canAccessMigrationDashboard, canAccessFSTReport, canAccessNapLocator, hasPerm } = useAuth();
  // Helper: true for both FST and FST MIG roles (case-insensitive)
  const isFSTRole = (() => {
    const rn = (user?.role_name || "").toLowerCase();
    return rn === "fst" || rn === "fst migration";
  })();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const navigateTo = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  // Non-mobile-only roles can navigate to admin pages
  const showAdminNav = !isMobileRole;

  return (
    <>
      <nav className="bg-blue-700 text-white shadow sticky top-0 z-[2000]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded overflow-hidden flex-shrink-0 bg-white/10">
              <img src="/best-icon.png" alt="BEST Inc." className="h-full w-full object-contain" />
            </div>
            <span className="text-base font-semibold">BEST DMS</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-white/80 truncate max-w-[120px]">{user?.display_name || user?.full_name}</span>
            <NotificationBell />
            <div className="relative">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded bg-blue-800 active:bg-blue-900"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-56 bg-white text-black rounded-lg shadow-lg overflow-hidden z-50 max-h-[80vh] overflow-y-auto">
                  <div className="px-4 py-3 border-b bg-gray-50">
                    <p className="text-sm font-medium text-gray-900 truncate">{user?.display_name || user?.full_name}</p>
                    {!isFSTRole && <p className="text-xs text-gray-500">{user?.role_name || "Mobile User"}</p>}
                  </div>

                  {!showAdminNav && canAccessNIDashboard && (
                    <button
                      onClick={() => navigateTo("/m")}
                      className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/m" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                    >
                      New Installation
                    </button>
                  )}
                  {!showAdminNav && canAccessNiDashboard && (
                    <button
                      onClick={() => navigateTo("/ni/dashboard")}
                      className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/ni/dashboard" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                    >
                      NI Dashboard
                    </button>
                  )}

                  {!showAdminNav && canAccessFSTReport && (
                    <button
                      onClick={() => navigateTo("/reports/fst-daily-output")}
                      className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/reports/fst-daily-output" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                    >
                      Reports
                    </button>
                  )}
                  {!showAdminNav && canAccessNapLocator && (
                    <button
                      onClick={() => navigateTo("/nap-locator")}
                      className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/nap-locator" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                    >
                      NAP Locator
                    </button>
                  )}

                  {showAdminNav && (
                    <>
                      {canAccessDashboard && (
                        <button
                          onClick={() => navigateTo("/m")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/m" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          Dashboard
                        </button>
                      )}
                      {canAccessNiDashboard && (
                        <button
                          onClick={() => navigateTo("/ni/dashboard")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/ni/dashboard" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          NI Dashboard
                        </button>
                      )}
                      {canAccessXlsx && (
                        <button
                          onClick={() => navigateTo("/xlsx-imports")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/xlsx-imports" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          BO Imports
                        </button>
                      )}
                      {canAccessManageAccounts && (
                        <button
                          onClick={() => navigateTo("/manage-accounts")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/manage-accounts" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          Manage Accounts
                        </button>
                      )}
                      {canViewRoles && (
                        <button
                          onClick={() => navigateTo("/roles")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/roles" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          Manage Roles
                        </button>
                      )}
                      {hasPerm("page.team_leaders") && (
                        <button
                          onClick={() => navigateTo("/team-leaders")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/team-leaders" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          Team Leaders
                        </button>
                      )}
                      {hasPerm("page.teams") && (
                        <button
                          onClick={() => navigateTo("/teams")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/teams" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          Teams
                        </button>
                      )}
                      {hasPerm("page.companies") && (
                        <button
                          onClick={() => navigateTo("/companies")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/companies" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          Companies
                        </button>
                      )}
                      {canAccessDatabaseBackups && (
                        <button
                          onClick={() => navigateTo("/database-backups")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/database-backups" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          Backups
                        </button>
                      )}
                      {canAccessJoRegistry && ENABLE_JO_REGISTRY && (
                        <button
                          onClick={() => navigateTo("/jo-registry")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/jo-registry" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          JO Registry
                        </button>
                      )}
                      {canViewLoginActivity && (
                        <button
                          onClick={() => navigateTo("/login-activity")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/login-activity" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          Login Activity
                        </button>
                      )}
                      {canAccessReports && (
                        <button
                          onClick={() => navigateTo("/reports")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/reports" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          Reports
                        </button>
                      )}
                      {canAccessNapLocator && (
                        <button
                          onClick={() => navigateTo("/nap-locator")}
                          className={`w-full text-left px-4 py-3 text-sm active:bg-gray-200 ${location.pathname === "/nap-locator" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}
                        >
                          NAP Locator
                        </button>
                      )}
                      <div className="border-t" />
                    </>
                  )}

                  {(canAccessMigration || canAccessMigrationDashboard) && (
                    <>
                      {!isFSTRole && <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Migration</div>}
                      {canAccessMigrationDashboard && (
                        <button onClick={() => navigateTo("/migration/dashboard")} className={`w-full text-left ${isFSTRole ? 'px-4 py-3' : 'px-6 py-2.5'} text-sm font-medium active:bg-gray-200 ${location.pathname === "/migration/dashboard" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}>Migration Dashboard</button>
                      )}
                      {hasPerm("migration.view_uploaded") && (
                        <button onClick={() => navigateTo("/migration/uploaded")} className={`w-full text-left ${isFSTRole ? 'px-4 py-3' : 'px-6 py-2.5'} text-sm active:bg-gray-200 ${location.pathname === "/migration/uploaded" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}>Uploaded for Migration</button>
                      )}
                      {hasPerm("migration.view_calling") && (
                        <button onClick={() => navigateTo("/migration/for-calling")} className={`w-full text-left ${isFSTRole ? 'px-4 py-3' : 'px-6 py-2.5'} text-sm active:bg-gray-200 ${location.pathname === "/migration/for-calling" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}>For Calling/Scheduling</button>
                      )}
                      {hasPerm("migration.view_dispatch") && (
                        <button onClick={() => navigateTo("/migration/for-dispatch")} className={`w-full text-left ${isFSTRole ? 'px-4 py-3' : 'px-6 py-2.5'} text-sm active:bg-gray-200 ${location.pathname === "/migration/for-dispatch" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}>Migration</button>
                      )}
                      {hasPerm("migration.view_visited") && (
                        <button onClick={() => navigateTo("/migration/visited-called")} className={`w-full text-left ${isFSTRole ? 'px-4 py-3' : 'px-6 py-2.5'} text-sm active:bg-gray-200 ${location.pathname === "/migration/visited-called" ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-100"}`}>Completed/Visited/Called</button>
                      )}
                      <div className="border-t" />
                    </>
                  )}

                  <button
                    onClick={() => { setMenuOpen(false); setShowChangePassword(true); }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 text-sm active:bg-gray-200"
                  >
                    Change Password
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); handleLogout(); }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 text-sm text-red-600 active:bg-red-50"
                  >
                    Logout
                  </button>
                  <div className="px-4 py-2 text-xs text-gray-400 border-t text-center select-none">
                    v{APP_VERSION}
                  </div>
                </div>
              </>
            )}
          </div>
          </div>
        </div>
      </nav>

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}
